import React from 'react';
import { OrgTreeNode } from './OrgTreeNode';
import type { OrgUnit } from '@schemas/api';
import { useOrgStore } from '@store/orgStore';
import { useNotificationStore } from '@store/notificationStore';

interface OrgTreeExplorerProps {
  units: OrgUnit[];
  isLoading: boolean;
  canEdit: boolean;
  onUnitSelect: (unitId: string) => void;
  onUnitMove: (unitId: string, newParentId: string | null) => void;
}

export const OrgTreeExplorer: React.FC<OrgTreeExplorerProps> = ({
  units,
  isLoading,
  canEdit,
  onUnitSelect,
  onUnitMove,
}) => {
  const toggleNodeExpanded = useOrgStore((state) => state.toggleNodeExpanded);
  const setDraggedUnitId = useOrgStore((state) => state.setDraggedUnitId);
  const { addNotification } = useNotificationStore();

  const handleDragStart = (e: React.DragEvent, unitId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', unitId);
    setDraggedUnitId(unitId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetUnitId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const draggedId = e.dataTransfer.getData('text/plain');
    setDraggedUnitId(null);

    if (!draggedId || draggedId === targetUnitId) {
      return;
    }

    // Validation: prevent dragging parent to child
    const draggedUnit = findUnit(units, draggedId);
    const targetUnit = findUnit(units, targetUnitId);

    if (!draggedUnit || !targetUnit) {
      addNotification({
        message: 'Invalid drop operation',
        type: 'error',
      });
      return;
    }

    // Check if target is a descendant of dragged unit
    if (isDescendant(draggedUnit, targetUnit.id)) {
      addNotification({
        message: 'Cannot move a unit to its own descendant',
        type: 'error',
      });
      return;
    }

    // Call the move handler
    onUnitMove(draggedId, targetUnitId);
  };

  const findUnit = (unitList: OrgUnit[], unitId: string): OrgUnit | null => {
    for (const unit of unitList) {
      if (unit.id === unitId) return unit;
      if (unit.children) {
        const found = findUnit(unit.children, unitId);
        if (found) return found;
      }
    }
    return null;
  };

  const isDescendant = (parent: OrgUnit, targetId: string): boolean => {
    if (!parent.children) return false;
    for (const child of parent.children) {
      if (child.id === targetId) return true;
      if (isDescendant(child, targetId)) return true;
    }
    return false;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading hierarchy...</div>
      </div>
    );
  }

  if (!units || units.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No units in this hierarchy</div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto bg-white rounded-lg border border-gray-200 p-3"
      onDragOver={handleDragOver}
    >
      {units.map((unit, index) => (
        <OrgTreeNode
          key={unit.id}
          unit={unit}
          onSelect={onUnitSelect}
          onToggleExpand={toggleNodeExpanded}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          canEdit={canEdit}
          isLast={index === units.length - 1}
        />
      ))}
    </div>
  );
};
